import { Injectable } from '@angular/core';
import { Charges, Product, SummaryDetail } from '@models';
import { FundSummaryService } from '@products/services/fund-summary.service';
import { SiteConfigService } from '@services/site-config.service';
import {
  ConfigurationId,
  GridDiv,
  OverviewData,
  OverviewSection,
  OverviewState,
  ProductDetailConfiguration,
  ShareClassCode,
} from '@types';
import { Logger } from '@utils/logger';
import { Subject } from 'rxjs';
import fundSummaryDetails from '@graphql/overview/fund-summary.graphql';
import { INITIAL_STATE } from './overview.config';
import { hasValue } from '@products/utils/mappers/mapper.utils';

const logger = Logger.getLogger('SummaryService');
export const FUND_INFO_COLUMN_MAX_SIZE = 5;
export const DISTRIBUTIONS_COLUMN_MAX_SIZE = 5;

@Injectable({
  providedIn: 'root',
})
export class SummaryService {
  private state: OverviewState;
  private configurationName: ConfigurationId;
  private shareClassCode: ShareClassCode;
  overviewState: Subject<OverviewState>;

  constructor(
    private fundSummaryService: FundSummaryService,
    private siteConfigService: SiteConfigService
  ) {
    this.state = INITIAL_STATE;
    this.overviewState = new Subject();
  }

  /**
   * Call register method of fund summary service.
   */
  public populate(
    productDetailConfiguration: ProductDetailConfiguration
  ): void {
    this.shareClassCode = productDetailConfiguration.shareClassCode;

    this.fundSummaryService
      .register(fundSummaryDetails, {
        fundId: productDetailConfiguration.fundId,
        shareClassCode: productDetailConfiguration.shareClassCode,
      })
      .subscribe((mergedResponse) => {
        logger.debug(mergedResponse);
        this.mapSummaryAndState(mergedResponse);
      });
  }

  /**
   * Map the fund overview & summary response.
   * Map the decorated config and any other setting to the state.
   * @param mergedResponse received fund overview & summary details response
   */
  mapSummaryAndState(mergedResponse: [Product, SummaryDetail[]]) {
    const [overview, summaryDetails] = mergedResponse;

    const charges: Charges = overview?.shareClasses?.find(
      (shareClass) => shareClass.shareClassCode === this.shareClassCode
    ).charges;
    this.state.chargesInfo = {
      waiverType: charges?.waiverType,
      waiverEndDate: charges?.waiverEndDate,
    };

    this.state.isBrazil = this.siteConfigService.isBrazil();
    this.state.data = this.mapSummaryDetailsToStateBySection(summaryDetails);
    this.state.gridDiv = this.calculateGridDiv(this.state.data);
    this.state.siteConfig = this.siteConfigService.product?.site;

    this.state.hasLoadedFullFeed = true;
    this.overviewState.next(this.state);
  }

  /**
   * Filter summary details section wise & sort it in ascending order as per sort-order.
   * @param fundSummaryData raw fund summary data to be filtered as per section
   */
  mapSummaryDetailsToStateBySection(
    fundSummaryData: SummaryDetail[]
  ): OverviewData {
    const fundInformationDetails: SummaryDetail[] = [];
    const salesChargesDetails: SummaryDetail[] = [];
    const identifiersDetails: SummaryDetail[] = [];
    const distributionsDetails: SummaryDetail[] = [];
    const tradingCharacteristicsDetails: SummaryDetail[] = [];
    const temitKeyDateDetails: SummaryDetail[] = [];

    fundSummaryData.forEach((summaryElement: SummaryDetail) => {
      switch (summaryElement.sectionStd) {
        case 'FUNDINFORMATION':
          fundInformationDetails.push(summaryElement);
          break;
        case 'SALESCHARGESANDFEES':
          // NGC-5961 filtered out FEE_CONTRACT_VOLUNTARY based on discussion with pds.
          if (summaryElement.elementName !== 'FEE_CONTRACT_VOLUNTARY') {
            salesChargesDetails.push(summaryElement);
          }
          break;
        case 'IDENTIFIERS':
          identifiersDetails.push(summaryElement);
          break;
        case 'YIELDSANDDISTRIBUTIONS':
          distributionsDetails.push(summaryElement);
          break;
        case 'TRADINGCHARACTERISTICS':
          tradingCharacteristicsDetails.push(summaryElement);
          break;
        case 'KEYDATES':
          temitKeyDateDetails.push(summaryElement);
          break;
      }
    });

    // Sort the individual list in ascending order as per 'sortOrder'.
    fundInformationDetails.sort((current, next) => {
      let c = Number(current.sortOrder);
      let n = Number(next.sortOrder);
      if (current.benchmarkOrder) {
        c += current.benchmarkOrder / 100;
      }
      if (next.benchmarkOrder) {
        n += next.benchmarkOrder / 100;
      }
      return c - n;
    });
    salesChargesDetails.sort(
      (current, next) => Number(current.sortOrder) - Number(next.sortOrder)
    );
    identifiersDetails.sort(
      (current, next) => Number(current.sortOrder) - Number(next.sortOrder)
    );
    distributionsDetails.sort(
      (current, next) => Number(current.sortOrder) - Number(next.sortOrder)
    );
    tradingCharacteristicsDetails.sort(
      (current, next) => Number(current.sortOrder) - Number(next.sortOrder)
    );
    temitKeyDateDetails.sort(
      (current, next) => Number(current.sortOrder) - Number(next.sortOrder)
    );

    return {
      fundInformation: this.getOverviewSection(fundInformationDetails),
      salesCharges: this.getOverviewSection(
        salesChargesDetails,
        this.siteConfigService.isSiteInternational()
          ? 'products.updated-MONTHLY'
          : 'products.updated-ANNUALLY'
      ),
      identifiers: this.getOverviewSection(identifiersDetails),
      distributions: this.getOverviewSection(distributionsDetails),
      tradingCharacteristics: this.getOverviewSection(
        tradingCharacteristicsDetails,
        `products.updated-${tradingCharacteristicsDetails[0]?.preelemValue}`
      ),
      temitKeyDateDetails: this.getOverviewSection(temitKeyDateDetails),
    };
  }

  private getOverviewSection(
    summaryDetails: SummaryDetail[],
    updateFrequency: string = null
  ): OverviewSection {
    let asOfDate: string;

    switch (summaryDetails[0]?.sectionStd) {
      // NGC-5205
      case 'SALESCHARGESANDFEES': {
        const asOfDateIndex = summaryDetails.findIndex(
          (detail) => detail.elementNameStd === 'REPORT_DATE'
        );
        const summaryDetail =
          asOfDateIndex > -1
            ? summaryDetails.splice(asOfDateIndex, 1)[0]
            : null;

        if (summaryDetail && hasValue(summaryDetail, 'elementValue')) {
          asOfDate = summaryDetail.elementValue; // Ref. This field will have as of date value in case of 'SALESCHARGESANDFEES'.
        }
        break;
      }
      // NGC-8404
      case 'TRADINGCHARACTERISTICS': {
        // NGC-8900: Fetch from "Daily Volume" if available, else default to first available object.
        const summaryDetailObj: SummaryDetail = summaryDetails.find(
          (summaryDetail) =>
            summaryDetail.elementNameStd === 'DAILY_SHARE_VOLUME'
        );

        asOfDate = summaryDetailObj
          ? summaryDetailObj.asOfDate
          : summaryDetails[0].asOfDate;
      }
    }

    return {
      items: summaryDetails,
      asOfDate,
      updateFrequency,
    };
  }

  /**
   * Calculate grid division as per enum defined.
   */
  calculateGridDiv(overviewData: OverviewData): GridDiv {
    let gridDiv: GridDiv;

    // if, fund-info item size is within limit.
    if (
      overviewData.fundInformation.items.length <= FUND_INFO_COLUMN_MAX_SIZE
    ) {
      // if, 2nd column section data is available.
      if (
        this.siteConfigService.isBrazil() &&
        (overviewData.salesCharges.items.length > 0 ||
          overviewData.tradingCharacteristics.items.length > 0)
      ) {
        gridDiv = GridDiv.TWO_EQUAL;
      } else if (
        !this.siteConfigService.isBrazil() &&
        (overviewData.salesCharges.items.length > 0 ||
          overviewData.identifiers.items.length > 0 ||
          overviewData.tradingCharacteristics.items.length > 0)
      ) {
        gridDiv = GridDiv.TWO_EQUAL;
      } else {
        gridDiv = GridDiv.ONE;
      }
    } else {
      if (
        this.siteConfigService.isBrazil() &&
        (overviewData.salesCharges.items.length > 0 ||
          overviewData.tradingCharacteristics.items.length > 0)
      ) {
        gridDiv = GridDiv.TWO_SPLIT;
      } else if (
        !this.siteConfigService.isBrazil() &&
        (overviewData.salesCharges.items.length > 0 ||
          overviewData.identifiers.items.length > 0 ||
          overviewData.tradingCharacteristics.items.length > 0)
      ) {
        gridDiv = GridDiv.TWO_SPLIT;
      } else {
        gridDiv = GridDiv.ONE_SPLIT;
      }
    }

    return gridDiv;
  }

  setConfigurationName(configurationName: ConfigurationId) {
    this.configurationName = configurationName;
  }

  getConfigurationName(): ConfigurationId {
    return this.configurationName;
  }
}
